import api from './api';

export const OFFER_DRAFT_TIMEOUT_MS = 15000;

/**
 * Service for a student's candidacies: their applications to positions
 * (awarded course sections). The server decides whether a position is open to
 * the student; a position they may not see answers 404 whatever the reason.
 */
export const candidacyService = {
    /**
     * Apply to a position
     * @param {string} applicationId - the awarded course support application
     * @param {string} program - 'si' or 'la'
     * @param {{ commitmentConfirmed: boolean, courseCompleted?: boolean }} answers
     *   what the student confirmed when applying: they can attend the meeting
     *   times and commit the hours, and whether they completed the course
     *   when the program asks it
     */
    async create(applicationId, program, answers) {
        const response = await api.post('/candidacy', {
            applicationId,
            program,
            ...answers,
        });
        return response.data;
    },

    /**
     * Resubmit a withdrawn application to a position. The server revives the
     * same candidacy through the apply route's own checks, and answers 409 if
     * the candidacy is not withdrawn.
     * @param {string} applicationId - the awarded course support application
     * @param {string} program - 'si' or 'la'
     * @param {{ commitmentConfirmed: boolean, courseCompleted?: boolean }} answers
     */
    async resubmit(applicationId, program, answers) {
        return this.create(applicationId, program, answers);
    },

    /**
     * The caller's own candidacies, as { candidacies, total }
     */
    async getMine() {
        const response = await api.get('/candidacy/mine');
        return response.data;
    },

    /**
     * Withdraw from one position
     */
    async withdraw(candidacyId) {
        const response = await api.post(`/candidacy/${candidacyId}/withdraw`);
        return response.data;
    },

    /**
     * A position's candidates as the caller may see them, as
     * { position, candidacies, total }. The server answers 404 for a position
     * the caller may not see and 403 for one whose candidates are not shared
     * with them.
     * @param {string} applicationId - the awarded course support application
     * @param {string} program - 'si' or 'la'
     */
    async listForPosition(applicationId, program) {
        const response = await api.get(`/candidacy/position/${applicationId}`, {
            params: { program },
        });
        return response.data;
    },

    /**
     * Move a candidacy to another stage. Each candidacy the server returns
     * lists the stages the caller may move it to (allowedMoves).
     * @param {string} candidacyId
     * @param {string} stage
     * @param {string} [note] - reviewers' note on the move; left out when undefined
     */
    async moveStage(candidacyId, stage, note) {
        const body = note === undefined ? { stage } : { stage, note };
        const response = await api.post(
            `/candidacy/${candidacyId}/stage`,
            body
        );
        return response.data;
    },

    /**
     * The general pool for a program and term, as { pool, total }: students
     * who answered Yes to being considered for any open position and hold no
     * accepted position. Coordinators and admins only. Empty filters are not
     * sent.
     * @param {{ program: string, term: string, day?: string,
     *   minHours?: string|number, subject?: string }} query
     */
    async getPool({ program, term, day, minHours, subject }) {
        const params = { program, term };
        if (day) params.day = day;
        if (minHours !== undefined && minHours !== '') {
            params.minHours = minHours;
        }
        if (subject) params.subject = subject;
        const response = await api.get('/candidacy/pool', { params });
        return response.data;
    },

    /**
     * Give a pool member a candidacy at one open position (source
     * 'matched'). Answers the candidacy as a coordinator reads it; a refusal
     * is a 409 whose message says why.
     * @param {string} studentApplicationId - the pool member's application
     * @param {string} applicationId - the awarded course support application
     */
    async addToPosition(studentApplicationId, applicationId) {
        const response = await api.post('/candidacy/pool/add', {
            studentApplicationId,
            applicationId,
        });
        return response.data;
    },

    /**
     * Add feedback to a candidacy
     * @param {string} candidacyId
     * @param {string} text
     * @param {string} [visibility] - 'internal' (the default) or 'shared'
     */
    async addFeedback(candidacyId, text, visibility) {
        const response = await api.post(`/candidacy/${candidacyId}/feedback`, {
            text,
            visibility,
        });
        return response.data;
    },

    /**
     * Set the reviewers' rank of a candidacy, or clear it with null
     */
    async setRank(candidacyId, positionRank) {
        const response = await api.put(`/candidacy/${candidacyId}/rank`, {
            positionRank,
        });
        return response.data;
    },

    /**
     * Replace a candidacy's interview details
     * @param {string} candidacyId
     * @param {{ when?: string, where?: string, details?: string }} interview
     */
    async setInterview(candidacyId, interview) {
        const { when, where, details } = interview;
        const response = await api.put(`/candidacy/${candidacyId}/interview`, {
            when,
            where,
            details,
        });
        return response.data;
    },

    /**
     * Offer a candidate the position
     * @param {string} candidacyId
     * @param {{ message?: string, respondByDay?: string, releaseFrom?: string }}
     *   [options] - `message` is the email text after the sentence stating the
     *   offer (with none the server fills the program's template);
     *   `respondByDay` is the last day to answer, YYYY-MM-DD (with none the
     *   program's default); `releaseFrom` is an expired offer whose place
     *   this offer takes over in one step. A key left undefined is not sent.
     */
    async offer(candidacyId, { message, respondByDay, releaseFrom } = {}) {
        const body = {};
        if (typeof message === 'string') body.message = message;
        if (respondByDay) body.respondByDay = respondByDay;
        if (releaseFrom) body.releaseFrom = releaseFrom;
        const url = `/candidacy/${candidacyId}/offer`;
        const response =
            Object.keys(body).length > 0
                ? await api.post(url, body)
                : await api.post(url);
        return response.data;
    },

    /**
     * The program's offer email text for a candidacy, filled from its
     * template, as { message, fixedParagraphs, respondByDay, respondBy,
     * earliestDay, latestDay }. Writes nothing.
     * @param {string} candidacyId
     * @param {string} [releaseFrom] - an expired offer whose place this offer
     *   would take over; without it a position with no open place refuses
     */
    async getOfferDraft(candidacyId, releaseFrom) {
        const response = await api.get(
            `/candidacy/${candidacyId}/offer-draft`,
            {
                timeout: OFFER_DRAFT_TIMEOUT_MS,
                ...(releaseFrom && { params: { releaseFrom } }),
            }
        );
        return response.data;
    },

    /**
     * The student's copy of the offer email as it is sent if the offer is
     * made now with this message, as { emails }. Writes nothing.
     * @param {string} candidacyId
     * @param {string} message
     * @param {string} [respondByDay] - YYYY-MM-DD; left out when empty
     * @param {string} [releaseFrom] - as for getOfferDraft
     */
    async previewOffer(candidacyId, message, respondByDay, releaseFrom) {
        const body = {
            message,
            ...(respondByDay && { respondByDay }),
            ...(releaseFrom && { releaseFrom }),
        };
        const response = await api.post(
            `/candidacy/${candidacyId}/offer-preview`,
            body
        );
        return response.data;
    },

    /**
     * The student's weekly hours, as an offer-maker for the position may see
     * them: { hoursPerWeek, withinLimits } for the section's faculty, with
     * the held hours and the limit for the program's coordinators. The server
     * answers 403 to anyone who may not make offers for the position.
     * @param {string} candidacyId
     */
    async getCommitment(candidacyId) {
        const response = await api.get(`/candidacy/${candidacyId}/commitment`);
        return response.data;
    },

    /**
     * A candidate's resume file as a blob, for a reviewer of the position. It
     * needs the signed-in user's token, which a plain link cannot send.
     * @param {string} candidacyId
     * @returns {Promise<Blob>}
     */
    async downloadResume(candidacyId) {
        const response = await api.get(`/candidacy/${candidacyId}/resume`, {
            responseType: 'blob',
        });
        return response.data;
    },

    /**
     * Take back an offer the student has not answered. The candidacy returns
     * to finalist and its place opens.
     * @param {string} candidacyId
     * @param {string} [note] - reviewers' note on the take-back; left out when undefined
     */
    async takeBackOffer(candidacyId, note) {
        const body = note === undefined ? {} : { note };
        const response = await api.post(
            `/candidacy/${candidacyId}/offer/take-back`,
            body
        );
        return response.data;
    },

    /**
     * The student answers their own offer
     * @param {string} candidacyId
     * @param {string} action - 'accept' or 'decline'
     * @param {string} offeredAt - the offer's date exactly as the student's list gave it
     * @param {{ reasonCode: string, reasonText?: string }} [reason] - why the
     *   student declines; its fields join the body only when given
     */
    async respond(candidacyId, action, offeredAt, reason) {
        const body = { action, offeredAt };
        if (reason) {
            body.reasonCode = reason.reasonCode;
            if (typeof reason.reasonText === 'string') {
                body.reasonText = reason.reasonText;
            }
        }
        const response = await api.post(
            `/candidacy/${candidacyId}/respond`,
            body
        );
        return response.data;
    },

    /**
     * End a place or an offer with a reason. The student leaves their own
     * accepted position; a reviewer rescinds an offer or releases an
     * accepted place. A reviewer's response carries nextCandidate. The
     * server refuses (409) when the candidacy is no longer what the caller
     * was shown.
     * @param {string} candidacyId
     * @param {{ reasonCode: string, reasonText?: string }} reason
     * @param {{ stage: string, offeredAt?: string|null }} shown - the stage
     *   the caller was shown and, for an offer, its date exactly as the list
     *   gave it
     */
    async release(candidacyId, { reasonCode, reasonText }, shown) {
        const body = { reasonCode, stage: shown.stage };
        if (typeof reasonText === 'string') body.reasonText = reasonText;
        if (shown.stage === 'offered') body.offeredAt = shown.offeredAt ?? null;
        const response = await api.post(
            `/candidacy/${candidacyId}/release`,
            body
        );
        return response.data;
    },

    /**
     * Give an expired offer's place back to the students who applied. The
     * candidacy closes and the place opens.
     * @param {string} candidacyId
     */
    async releaseExpired(candidacyId) {
        const response = await api.post(
            `/candidacy/${candidacyId}/release`,
            {}
        );
        return response.data;
    },
};
